import { ArrowRight, Clock, Route } from "lucide-react";
import { Link } from "react-router-dom";

import { DayChips } from "@/components/groups/day-chips";
import { RouteLine } from "@/components/groups/route-line";
import { UserAvatar } from "@/components/user-avatar";
import { Badge } from "@/components/ui/badge";
import { t } from "@/i18n/pl";
import { formatTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { GroupSummary } from "@/types/api";

/** One car on the browse list. The whole card is the tap target. */
export function GroupCard({ group, index = 0 }: { group: GroupSummary; index?: number }) {
  const isFull = group.seats_free === 0;

  return (
    <Link
      to={`/grupy/${group.id}`}
      style={{ animationDelay: `${Math.min(index, 6) * 60}ms` }}
      className={cn(
        "animate-in fade-in slide-in-from-bottom-2 fill-mode-both group block duration-500",
        // min-w-0 is load-bearing: grid items default to min-width:auto, so
        // without it the badges' min-content width pushes the card past the
        // viewport on phones instead of wrapping.
        "bg-card min-w-0 rounded-xl border p-4 shadow-xs transition-all",
        "hover:-translate-y-0.5 hover:shadow-md",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
        // The match is the whole point of browsing: a gold edge makes the
        // "this car passes your town" cards read before any text does.
        group.on_your_route && "border-[oklch(0.7187_0.0914_74.88)] shadow-sm",
      )}
    >
      {/* The driver's name owns the header row alone. Sharing it with the
          badges truncated exactly the thing a seeker scans for ("Mił…"), and
          on a phone the two badges together leave a name almost no room. */}
      <div className="flex items-center gap-3">
        <UserAvatar user={group.driver} className="size-11 shrink-0" fallbackClassName="bg-primary text-primary-foreground font-semibold" />

        <div className="min-w-0 flex-1">
          <p className="font-semibold">{group.driver.full_name}</p>
          <p className="text-muted-foreground truncate text-xs">{group.car_model}</p>
        </div>
      </div>

      {/* Own row, card-edge aligned like everything below it, so both badges
          fit side by side and wrap only when the card is genuinely narrow. */}
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {group.on_your_route && (
          <Badge className="bg-[oklch(0.7187_0.0914_74.88)] text-[oklch(0.217_0.0936_260.97)]">
            <Route aria-hidden /> {t.group.onYourRoute}
          </Badge>
        )}
        <Badge
          variant={isFull ? "secondary" : "default"}
          className={cn(!isFull && "bg-[oklch(0.42_0.1_124)]")}
        >
          {t.group.seatsFree(group.seats_free)}
        </Badge>
      </div>

      <RouteLine from={group.pickup_area} to={group.plant} className="mt-2.5" />

      <DayChips days={group.days} className="mt-2" />

      <div className="text-muted-foreground mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-2.5 text-sm">
        <span className="flex items-center gap-1.5 font-medium tabular-nums">
          <Clock className="size-4" aria-hidden />
          {t.group.departure} {formatTime(group.departure_time)}
        </span>
        <span className="tabular-nums">
          {t.group.return} {formatTime(group.return_time)}
        </span>
        <ArrowRight
          className="ml-auto size-4 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </div>
    </Link>
  );
}
